import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WorkforceCardsLayout } from "@/components/rdo/WorkforceCardsLayout";
import { EquipmentCardsLayout } from "@/components/rdo/EquipmentCardsLayout";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Cloud, 
  Sun, 
  Users, 
  Timer, 
  FileText, 
  MessageSquare,
  Camera,
  Video,
  Paperclip,
  Signature,
  Thermometer
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

// Mock data - in real app, this would come from API
const mockRDOData = {
  obraName: "Edifício Residencial Vista Verde",
  client: "Incorporadora ABC Ltda.",
  address: "Rua das Palmeiras, 123 - São Paulo/SP",
  responsible: "João Silva",
  reportNumber: "RDO-2024-001",
  date: "2024-01-15",
  weekDay: "Segunda-feira",
  contractNumber: "CT-2023-456",
  contractDeadline: "2024-12-31",
  elapsedTime: "15 dias",
  remainingTime: "350 dias",
  
  weather: {
    morning: {
      condition: "Ensolarado",
      workable: true,
      observation: "Condições ideais para trabalho"
    },
    afternoon: {
      condition: "Parcialmente nublado",
      workable: true,
      observation: "Boas condições para trabalho"
    }
  },
  
  workforce: [
    {
      team: "Equipe A - Estrutura",
      function: "Pedreiro",
      quantity: 5,
      entry: "07:00",
      exit: "16:00",
      workedHours: 8,
      extraHours: 1,
      observations: "1 hora extra para conclusão da laje"
    },
    {
      team: "Equipe B - Elétrica",
      function: "Eletricista",
      quantity: 3,
      entry: "08:00",
      exit: "17:00",
      workedHours: 8,
      extraHours: 0
    }
  ],
  
  activities: [
    {
      activity: "Concretagem da laje do 3º andar",
      location: "Bloco A - 3º andar",
      start: "08:00",
      end: "14:00",
      completion: 100,
      description: "Concretagem realizada com sucesso. Volume de 15m³ de concreto usinado.",
      responsible: "João Silva"
    }
  ],
  
  equipment: [
    {
      type: "Betoneira",
      identification: "BT-001",
      operator: "Pedro Santos",
      entry: "07:30",
      exit: "15:30",
      workedHours: 8,
      idleTime: 0,
      condition: "Bom"
    }
  ],
  
  occurrences: [
    {
      type: "Segurança",
      description: "Trabalhador sem EPI completo",
      impact: "Baixo",
      actionTaken: "Orientação e fornecimento de EPI adequado",
      responsible: "Técnico de Segurança",
      photos: []
    }
  ],
  
  materials: {
    received: [
      {
        material: "Concreto Usinado FCK 25",
        unit: "m³",
        quantity: 15,
        document: "NF 12345",
        supplier: "Concreteira XYZ",
        time: "07:30",
        observation: "Material conforme especificação",
        status: "conforme" as const
      }
    ],
    used: [
      {
        material: "Cimento CP-II 50kg",
        unit: "sc",
        quantity: 20,
        location: "Bloco A - 3º andar",
        observation: "Utilizado na concretagem da laje"
      }
    ]
  },
  
  comments: [
    {
      id: "1",
      user: {
        id: "user1",
        name: "João Silva",
        role: "Engenheiro Responsável",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
      },
      datetime: "2024-01-15T14:30:00",
      text: "Concretagem realizada com sucesso.",
      canEdit: true
    }
  ],
  
  photos: [
    {
      id: "1",
      url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600",
      caption: "Concretagem da laje - Início dos trabalhos",
      timestamp: "2024-01-15T08:00:00"
    }
  ],
  
  videos: [
    {
      id: "1",
      url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
      thumbnail: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
      title: "Concretagem da laje - Processo completo",
      timestamp: "2024-01-15T08:00:00",
      duration: "3:45"
    }
  ],
  
  attachments: [
    {
      id: "1",
      name: "Laudo_Estrutural_Janeiro_2024.pdf",
      type: "PDF",
      size: "2.3 MB",
      url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
      timestamp: "2024-01-15T09:30:00",
      uploadedBy: "Carlos Silva",
      previewable: true
    }
  ],
  
  signatures: {
    workResponsible: {
      name: "Carlos Silva",
      document: "123.456.789-00",
      role: "Engenheiro de Obra",
      timestamp: "2024-01-15T18:00:00",
      signature: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjEwMCI+PHBhdGggZD0iTTEwLDUwIFE1MCwyMCA5MCw1MCBUMTcwLDUwIiBzdHJva2U9IiMwMDAiIHN0cm9rZS13aWR0aD0iMiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg=="
    }
  },
  
  status: "signed",
  approvers: ["user2", "user3"],
  signers: ["user1", "user2"]
};

export default function RDOPrint() {
  const { obraId, date: dateParam } = useParams();
  const [rdoData] = useState(mockRDOData);
  const currentDate = new Date(dateParam || rdoData.date);

  useEffect(() => {
    // Add print-specific styles
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-100 text-green-800 print-badge print:bg-gray-100 print:text-gray-800">Assinado</Badge>;
      case "approved":
        return <Badge className="bg-blue-100 text-blue-800 print-badge print:bg-gray-100 print:text-gray-800">Aprovado</Badge>;
      case "draft":
        return <Badge className="bg-yellow-100 text-yellow-800 print-badge print:bg-gray-100 print:text-gray-800">Rascunho</Badge>;
      case "in_review":
        return <Badge className="bg-blue-100 text-blue-800 print-badge print:bg-gray-100 print:text-gray-800">Em Revisão</Badge>;
      default:
        return <Badge variant="secondary" className="print-badge">{status}</Badge>;
    }
  };

  return (
    <div id="rdo-print-root" className="min-h-screen bg-white print:bg-white w-full">
      {/* Print Header - will be repeated on each page */}
      <div className="print-header hidden print:block">
        <div className="flex items-center justify-between border-b pb-4 mb-4">
          <div className="flex items-center gap-4">
            <img 
              src="https://via.placeholder.com/120x40/0ea5e9/ffffff?text=CONSTRUTORA" 
              alt="Logo da Construtora" 
              className="h-10"
              crossOrigin="anonymous"
            />
            <div>
              <h1 className="text-xl font-bold">Relatório Diário de Obra (RDO)</h1>
              <p className="text-sm text-gray-600">{rdoData.obraName}</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <div><strong>Data:</strong> {format(currentDate, "dd/MM/yyyy", { locale: ptBR })}</div>
            <div><strong>Relatório:</strong> {rdoData.reportNumber}</div>
          </div>
        </div>
      </div>

      {/* Rodapé HTML com data/hora */}
      <div id="print-footer" style={{
        position: 'fixed',
        bottom: '8mm',
        left: '15mm',
        right: '15mm',
        fontSize: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        borderTop: '1px solid #ccc',
        paddingTop: '4px'
      }} className="hidden print:flex">
        <span>Gerado em: {format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
        <span>RDO #{rdoData.reportNumber}</span>
      </div>

      <div className="max-w-full mx-auto space-y-6 print:space-y-4">
        
        {/* Header Principal */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <div className="flex items-center justify-center print:hidden">
              <div className="flex items-center gap-2">
                <img 
                  src="https://via.placeholder.com/120x40/0ea5e9/ffffff?text=CONSTRUTORA" 
                  alt="Logo da Construtora" 
                  className="h-8"
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="print:py-2">
            <div className="flex gap-6 print:block">
              {/* Metadados da Obra */}
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:grid-cols-2 print:gap-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Nome da Obra</label>
                    <p className="text-sm font-medium">{rdoData.obraName}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Cliente</label>
                    <p className="text-sm">{rdoData.client}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Endereço</label>
                    <p className="text-sm">{rdoData.address}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Responsável</label>
                    <p className="text-sm">{rdoData.responsible}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-muted-foreground">Status</label>
                    {getStatusBadge(rdoData.status)}
                  </div>
                </div>
              </div>

              {/* Tabela de Informações Contratuais */}
              <div className="w-72 print:w-full print:mt-4">
                <Table className="print:text-xs">
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Nº do Contrato</TableCell>
                      <TableCell className="text-xs py-1 px-2">{rdoData.contractNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Nº Relatório</TableCell>
                      <TableCell className="text-xs py-1 px-2">{rdoData.reportNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Data</TableCell>
                      <TableCell className="text-xs py-1 px-2">{format(currentDate, "dd/MM/yyyy", { locale: ptBR })}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Dia da Semana</TableCell>
                      <TableCell className="text-xs py-1 px-2">{format(currentDate, "EEEE", { locale: ptBR })}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Prazo contratual</TableCell>
                      <TableCell className="text-xs py-1 px-2">{rdoData.contractDeadline}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Prazo decorrido</TableCell>
                      <TableCell className="text-xs py-1 px-2 text-orange-600">{rdoData.elapsedTime}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium text-xs py-1 px-2 bg-muted">Prazo a vencer</TableCell>
                      <TableCell className="text-xs py-1 px-2 text-green-600">{rdoData.remainingTime}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Condições Climáticas */}
        <Card className="print:shadow-none print:border print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Cloud className="w-4 h-4" />
              Condições Climáticas
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="grid md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-xs">
                  <Sun className="w-3 h-3 text-yellow-500" />
                  Manhã
                </h4>
                <div className="space-y-1 text-xs">
                  <div><span className="font-medium">Tempo:</span> {rdoData.weather.morning.condition}</div>
                  <div><span className="font-medium">Condição:</span> 
                    <Badge variant={rdoData.weather.morning.workable ? "default" : "destructive"} className="ml-1 text-xs print-badge print:bg-gray-100 print:text-gray-800">
                      {rdoData.weather.morning.workable ? "Praticável" : "Impraticável"}
                    </Badge>
                  </div>
                  {rdoData.weather.morning.observation && (
                    <div><span className="font-medium">Observação:</span> {rdoData.weather.morning.observation}</div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2 text-xs">
                  <Cloud className="w-3 h-3 text-blue-500" />
                  Tarde
                </h4>
                <div className="space-y-1 text-xs">
                  <div><span className="font-medium">Tempo:</span> {rdoData.weather.afternoon.condition}</div>
                  <div><span className="font-medium">Condição:</span>
                    <Badge variant={rdoData.weather.afternoon.workable ? "default" : "destructive"} className="ml-1 text-xs print-badge print:bg-gray-100 print:text-gray-800">
                      {rdoData.weather.afternoon.workable ? "Praticável" : "Impraticável"}
                    </Badge>
                  </div>
                  {rdoData.weather.afternoon.observation && (
                    <div><span className="font-medium">Observação:</span> {rdoData.weather.afternoon.observation}</div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mão de Obra */}
        <Card className="print:shadow-none print:border print:border-gray-300 print:break-inside-avoid">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4" />
              Mão de Obra
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <WorkforceCardsLayout workforce={rdoData.workforce} showTotals={true} />
          </CardContent>
        </Card>

        {/* Atividades */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4" />
              Atividades
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="overflow-x-visible print:overflow-visible">
              <Table className="print:text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Atividade</TableHead>
                    <TableHead className="text-xs">Local/Frente</TableHead>
                    <TableHead className="text-xs">% Concluída</TableHead>
                    <TableHead className="text-xs">Responsável</TableHead>
                    <TableHead className="text-xs">Descrição/Observações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rdoData.activities.map((activity, index) => (
                    <TableRow key={index} className="print:break-inside-avoid">
                      <TableCell className="font-medium text-xs">{activity.activity}</TableCell>
                      <TableCell className="text-xs">{activity.location}</TableCell>
                      <TableCell>
                        <Badge variant={activity.completion === 100 ? "default" : "outline"} className="text-xs print-badge print:bg-gray-100 print:text-gray-800">
                          {activity.completion}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{activity.responsible}</TableCell>
                      <TableCell className="text-xs">{activity.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Equipamentos */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Timer className="w-4 h-4" />
              Equipamentos
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="overflow-x-visible print:overflow-visible">
              <Table className="print:text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Tipo</TableHead>
                    <TableHead className="text-xs">Identificação</TableHead>
                    <TableHead className="text-xs">Operador</TableHead>
                    <TableHead className="text-xs">Entrada</TableHead>
                    <TableHead className="text-xs">Saída</TableHead>
                    <TableHead className="text-xs">H. Trabalhadas</TableHead>
                    <TableHead className="text-xs">Tempo Parado</TableHead>
                    <TableHead className="text-xs">Condição</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rdoData.equipment.map((equip, index) => (
                    <TableRow key={index} className="print:break-inside-avoid">
                      <TableCell className="text-xs">{equip.type}</TableCell>
                      <TableCell className="text-xs">{equip.identification}</TableCell>
                      <TableCell className="text-xs">{equip.operator}</TableCell>
                      <TableCell className="text-xs">{equip.entry}</TableCell>
                      <TableCell className="text-xs">{equip.exit}</TableCell>
                      <TableCell className="text-xs">{equip.workedHours}</TableCell>
                      <TableCell className="text-xs">{equip.idleTime}</TableCell>
                      <TableCell className="text-xs">{equip.condition}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Ocorrências */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4" />
              Ocorrências
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="space-y-3 print:space-y-2">
              {rdoData.occurrences.map((occurrence, index) => (
                <div key={index} className="p-3 border rounded-lg print:break-inside-avoid print:border-gray-300">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="text-xs print:bg-gray-100 print:text-gray-800">{occurrence.type}</Badge>
                    <Badge variant={occurrence.impact === "Alto" ? "destructive" : occurrence.impact === "Médio" ? "secondary" : "default"} className="text-xs print:bg-gray-100 print:text-gray-800">
                      Impacto {occurrence.impact}
                    </Badge>
                  </div>
                  <h4 className="font-semibold mb-1 text-xs">{occurrence.description}</h4>
                  <div className="text-xs space-y-1">
                    <div><span className="font-medium">Ação Tomada:</span> {occurrence.actionTaken}</div>
                    <div><span className="font-medium">Responsável:</span> {occurrence.responsible}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Materiais Recebidos */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-sm">Materiais Recebidos</CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="overflow-x-visible print:overflow-visible">
              <Table className="print:text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs">Unidade</TableHead>
                    <TableHead className="text-xs">Quantidade</TableHead>
                    <TableHead className="text-xs">Documento</TableHead>
                    <TableHead className="text-xs">Fornecedor</TableHead>
                    <TableHead className="text-xs">Horário</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rdoData.materials.received.map((material, index) => (
                    <TableRow key={index} className="print:break-inside-avoid">
                      <TableCell className="text-xs">{material.material}</TableCell>
                      <TableCell className="text-xs">{material.unit}</TableCell>
                      <TableCell className="text-xs">{material.quantity}</TableCell>
                      <TableCell className="text-xs">{material.document}</TableCell>
                      <TableCell className="text-xs">{material.supplier}</TableCell>
                      <TableCell className="text-xs">{material.time}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="default" className="text-xs print:bg-gray-100 print:text-gray-800">
                          {material.status === 'conforme' ? 'Conforme' : 
                           material.status === 'aguardando_inspecao' ? 'Aguardando' : 'Recusado'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Materiais Utilizados */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="text-sm">Materiais Utilizados</CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="overflow-x-visible print:overflow-visible">
              <Table className="print:text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Material</TableHead>
                    <TableHead className="text-xs">Unidade</TableHead>
                    <TableHead className="text-xs">Quantidade</TableHead>
                    <TableHead className="text-xs">Local</TableHead>
                    <TableHead className="text-xs">Observação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rdoData.materials.used.map((material, index) => (
                    <TableRow key={index} className="print:break-inside-avoid">
                      <TableCell className="text-xs">{material.material}</TableCell>
                      <TableCell className="text-xs">{material.unit}</TableCell>
                      <TableCell className="text-xs">{material.quantity}</TableCell>
                      <TableCell className="text-xs">{material.location}</TableCell>
                      <TableCell className="text-xs">{material.observation}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Comentários */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="w-4 h-4" />
              Comentários
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="space-y-3 print:space-y-2">
              {rdoData.comments.map((comment) => (
                <div key={comment.id} className="flex gap-3 p-3 border rounded-lg print:break-inside-avoid print:border-gray-300">
                  <Avatar className="h-8 w-8 print:h-6 print:w-6">
                    <AvatarImage src={comment.user.avatar} crossOrigin="anonymous" />
                    <AvatarFallback className="text-xs">{comment.user.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-xs">{comment.user.name}</span>
                      <Badge variant="outline" className="text-xs print:bg-gray-100 print:text-gray-800">{comment.user.role}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(comment.datetime), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </span>
                    </div>
                    <p className="text-xs">{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Fotos */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Camera className="w-4 h-4" />
              Fotos ({rdoData.photos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-2">
              {rdoData.photos.map((photo) => (
                <div key={photo.id} className="space-y-2 print:break-inside-avoid">
                  <div className="aspect-video rounded-lg overflow-hidden border print:border-gray-300">
                    <img 
                      src={photo.url} 
                      alt={photo.caption}
                      className="w-full h-full object-cover print:max-w-full print:h-auto"
                      crossOrigin="anonymous"
                    />
                  </div>
                  <div className="text-xs space-y-1">
                    <p className="font-medium">{photo.caption}</p>
                    <p className="text-muted-foreground">
                      {format(new Date(photo.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Vídeos */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Video className="w-4 h-4" />
              Vídeos ({rdoData.videos.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-2">
              {rdoData.videos.map((video) => (
                <div key={video.id} className="space-y-2 print:break-inside-avoid">
                  <div className="aspect-video rounded-lg overflow-hidden border bg-gray-100 flex items-center justify-center print:border-gray-300">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                  </div>
                  <div className="text-xs space-y-1">
                    <p className="font-medium">{video.title}</p>
                    <div className="flex justify-between text-muted-foreground">
                      <span>{format(new Date(video.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}</span>
                      <span>{video.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Anexos */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4" />
              Anexos ({rdoData.attachments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="space-y-2">
              {rdoData.attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center gap-3 p-2 border rounded print:break-inside-avoid print:border-gray-300">
                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                    <Paperclip className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium truncate">{attachment.name}</span>
                      <Badge variant="outline" className="text-xs print:bg-gray-100 print:text-gray-800">{attachment.type}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {attachment.size} • {format(new Date(attachment.timestamp), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      {attachment.uploadedBy && ` • ${attachment.uploadedBy}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Assinaturas */}
        <Card className="print:shadow-none print:border print:border-gray-300">
          <CardHeader className="print:pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Signature className="w-4 h-4" />
              Assinaturas
            </CardTitle>
          </CardHeader>
          <CardContent className="print:py-2">
            <div className="grid md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2">
              <div className="border rounded-lg p-3 print:border-gray-300">
                <h4 className="font-semibold mb-2 text-xs">Responsável da Obra</h4>
                {rdoData.signatures.workResponsible ? (
                  <div className="space-y-1 text-xs">
                    <div><span className="font-medium">Nome:</span> {rdoData.signatures.workResponsible.name}</div>
                    <div><span className="font-medium">Documento:</span> {rdoData.signatures.workResponsible.document}</div>
                    <div><span className="font-medium">Função:</span> {rdoData.signatures.workResponsible.role}</div>
                    <div className="h-16 border rounded bg-muted/30 flex items-center justify-center print:border-gray-300">
                      <span className="text-muted-foreground text-xs">Assinatura Digital</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-16 border-2 border-dashed rounded flex items-center justify-center print:border-gray-300">
                    <span className="text-muted-foreground text-xs">Aguardando assinatura</span>
                  </div>
                )}
              </div>

              <div className="border rounded-lg p-3 print:border-gray-300">
                <h4 className="font-semibold mb-2 text-xs">Fiscal/Cliente</h4>
                <div className="h-16 border-2 border-dashed rounded flex items-center justify-center print:border-gray-300">
                  <span className="text-muted-foreground text-xs">Aguardando assinatura</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          @page {
            margin: 2cm 1.5cm;
            size: A4;
            
            @bottom-left {
              content: "Exportado em " date() " às " time();
              font-size: 10px;
              color: #666;
            }
            
            @bottom-right {
              content: "Página " counter(page) " de " counter(pages);
              font-size: 10px;
              color: #666;
            }
          }
          
          * {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          .print\\\\:break-inside-avoid {
            break-inside: avoid;
          }
          
          .print\\\\:break-after {
            break-after: page;
          }
          
          .print\\\\:overflow-visible {
            overflow: visible !important;
          }
          
          .print\\\\:max-h-none {
            max-height: none !important;
          }
        }
      `}} />
    </div>
  );
}